import React, { useState, useContext, useEffect } from "react"
import { customers } from "./FirebaseApp"
import { FirebaseContext } from "./Firebase"

import { useTranslation } from "next-i18next"
import dynamic from "next/dynamic"
import LoadingButton, { Button } from "./LoadingButton"
import firebase from "firebase/app"
import plans from "../functions/plans"
import { useRouter } from "next/router"

const LoadingSpinner = () => <svg className="animate-spin h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
</svg>

const SubscriptionLoadingButton = ({ onClick, children, disabled, highlight }) => {
  const [saveState, setSaveState] = useState(false)

  const onButtonClick = async () => {
    setSaveState("loading")
    await onClick()
    setSaveState("finished")
  }

  return (
    <button 
      onClick={onButtonClick}
      disabled={saveState === "loading" || disabled}
      className={`w-full ${highlight 
        ? "group relative flex justify-center items-center text-base rounded-full bg-gradient-to-r from-indigo-400 to-indigo-300 text-white hover:ring-2 hover:ring-blue-300 font-heading py-3 px-6"
        : "p-1"
      }`}
    >
      <div className={highlight ? "" : "group relative flex items-center py-2 px-5 text-sm sm:text-base rounded-full cursor-pointer font-heading hover:ring-2 ring-2 ring-gray-700 hover:ring-gray-500 mx-auto md:mx-0"}>
        <p className={`shrink-0 relative z-10 font-heading text-base  ${highlight ? "" : "text-black"} mx-auto flex items-center h-7`}>
          {saveState !== "loading" ? children : <LoadingSpinner />}
        </p>
      </div>
    </button>
  )
}

const BuyPlanButton = ({
  plan,
  text,
  isMonthly = false,
  success_url = `${window.location.origin}/thank-you?transaction_id=${Date.now()}&value=${isMonthly ? plan.price : plan.yearPrice}&currency=USD&plan=${plan.name}`,
  cancel_url = window.location.href,
  highlight
}) => {
  const auth = useContext(FirebaseContext)
  const { user } = auth
  const [error, setError] = useState(null)

  const initiateCheckout = async () => {
    setError(null)
    const customersRef = await customers()

    // Track beginning of checkout process in GA4
    if (window.gtag) {
      window.gtag('event', 'begin_checkout', {
        currency: 'USD',
        value: !isMonthly && plan.yearPrice ? plan.yearPrice : plan.price,
        items: [
          {
            item_name: plan.name,
            item_id: !isMonthly && plan.stripePriceIdYear ? plan.stripePriceIdYear : plan.stripePriceId,
            price: !isMonthly && plan.yearPrice ? plan.yearPrice : plan.price,
            quantity: 1
          }
        ]
      });
    }

    // Get Rewardful referral ID
    const rewardfulReferralId = window.Rewardful && window.Rewardful.referral;

    const checkoutSessionData = {
      price: !isMonthly && plan.stripePriceIdYear ? plan.stripePriceIdYear : plan.stripePriceId,
      mode: plan.paymentMode || 'subscription',
      success_url,
      cancel_url,
      allow_promotion_codes: true,
    };

    if (rewardfulReferralId) {
      checkoutSessionData.client_reference_id = rewardfulReferralId;
    }

    const customerDoc = await customersRef
      .doc(user.uid)
      .collection("checkout_sessions")
      .add(checkoutSessionData)

    try {
      await new Promise((resolve, reject) => {
        customerDoc.onSnapshot(snap => {
          const { error, url } = snap.data()
          if (error) {
            reject(error)
          }
          if (url) {
            // Track when checkout form is shown (checkout_progress)
            if (window.gtag) {
              window.gtag('event', 'checkout_progress', {
                checkout_step: 1,
                checkout_option: 'Stripe Checkout',
                currency: 'USD',
                value: !isMonthly && plan.yearPrice ? plan.yearPrice : plan.price,
                items: [
                  {
                    item_name: plan.name,
                    item_id: !isMonthly && plan.stripePriceIdYear ? plan.stripePriceIdYear : plan.stripePriceId,
                    price: !isMonthly && plan.yearPrice ? plan.yearPrice : plan.price,
                    quantity: 1
                  }
                ]
              });
            }
            window.location.assign(url)
            resolve()
          }
        })
      })
    } catch (error) {
      console.error(error)
      setError(error)
    }
  }

  return (
    <>
      {error && <p className="my-7 text-red-600">{error.message}</p>}
      <SubscriptionLoadingButton onClick={initiateCheckout} highlight={highlight}>
        {text}
      </SubscriptionLoadingButton>
    </>
  )
}

const ManagePlanButton = ({ highlight }) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)

  const openCustomerPanel = async () => {
    setIsLoading(true)
    await import("firebase/app")
    await import("firebase/functions")
    const functionRef = firebase
      .app()
      .functions("us-central1")
      .httpsCallable("ext-firestore-stripe-payments-ndm8-createPortalLink")
    const { data } = await functionRef({
      returnUrl: window.location.href,
      locale: "auto", // Optional, defaults to "auto"
      //   configuration: "bpc_1JSEAKHYgolSBA358VNoc2Hs", // Optional ID of a portal configuration: https://stripe.com/docs/api/customer_portal/configuration
    })
    window.location.assign(data.url)
  }

  return (
    <button 
      onClick={openCustomerPanel}
      disabled={isLoading}
      className={`w-full ${highlight 
        ? "group relative flex justify-center items-center text-base rounded-full bg-gradient-to-r from-indigo-400 to-indigo-300 text-white hover:ring-2 hover:ring-blue-300 font-heading py-3 px-6"
        : "p-1"
      }`}
    >
      <div className={highlight ? "" : "group relative flex items-center font-heading block py-2 px-5 text-sm sm:text-base rounded-full cursor-pointer font-heading hover:ring-2 ring-2 ring-gray-700 hover:ring-gray-500 mx-auto md:mx-0"}>
        <p className={`relative z-10 font-heading text-base ${highlight ? "" : "text-black"} mx-auto flex items-center h-8`}>
          {isLoading ? (
            <>
              <LoadingSpinner  />
            </>
          ) : (
            t("plans.manage")
          )}
        </p>
      </div>
    </button>
  )
}

const getCurrentYearMonth = () => {
  const date = new Date()
  const month = date.getUTCMonth() + 1 //months from 1-12
  const year = date.getUTCFullYear()
  return `${year}_${month}`
}

const SubscriptionDetails = props => {
  const auth = useContext(FirebaseContext)
  const { stripeRole, user } = auth
  const { t } = useTranslation()
  const [currentAiLimits, setCurrentAiLimits] = useState(0)
  const [currentBgRemovals, setCurrentBgRemovals] = useState(0)
  const [currentImportLimit, setCurrentImportLimit] = useState(0)

  const router = useRouter()

  const backgroundRemovaLimit = stripeRole
    ? plans[stripeRole].backgroundLimit
    : plans.trial.backgroundLimit

  const generateAiLimit = stripeRole
    ? plans[stripeRole].generateAiLimit
    : plans.trial.generateAiLimit

  const importLimit = stripeRole
    ? plans[stripeRole].importLimit
    : plans.trial.importLimit


  useEffect(() => {
    const loadCurrentRemovals = async () => {
      const customersRef = await customers()
      const customerDoc = await customersRef.doc(user.uid).get()
      const {
        background_removals,
        background_removals_byMonth,
        ai_generations_byMonth,

      } = customerDoc.data() || {}
      if(stripeRole) {
        setCurrentAiLimits(ai_generations_byMonth?.[getCurrentYearMonth()] || 0)
        setCurrentBgRemovals(background_removals_byMonth?.[getCurrentYearMonth()] || 0)
        // setCurrentImportLimit(?.[getCurrentYearMonth()] || 0) TODO: implement this
      }
      else setCurrentBgRemovals(background_removals || 0)
    }
    loadCurrentRemovals()
  }, [stripeRole])

  return (
    <>
      <div className="relative max-w-lg p-5 mx-6 rounded-xl shadow">
        <h3 className="text-xl text-gray-700 mb-2">{t("plans.title")}</h3>
        <h4 className="text-lg text-pink-800 capitalize mb-7">{stripeRole ? stripeRole : "Not Subscribed"}</h4>
        {stripeRole ? (
          <>
            <ProgressBar
              text="AI Generations"
              value={currentAiLimits}
              max={generateAiLimit}
            />
            <ProgressBar
              text={t("plans.background-removals")}
              value={currentBgRemovals}
              max={backgroundRemovaLimit}
            />
            {/* <ProgressBar
              text="Product Imports"
              value={currentImportLimit}
              max={importLimit}
            /> */}

            <p className="my-7 text-gray-600">
              Your <span className="capitalize">{stripeRole}</span> Plan
              includes a monthly allowance that resets on the first day of each
              month. For additional resources or to adjust your plan features,
              please select 'Manage my subscription.'
            </p>
          </>
        ) : (
          <p className="my-7 text-gray-600">
            Explore our range of plans designed to accommodate your requirements. Subscribe to gain access to exclusive features tailored to enhance your experience. For more information and to begin your subscription, please click the 'Upgrade' button below.
          </p>
        )}
        <div className="text-right">
          {stripeRole ? (
            <ManagePlanButton />
          ) : (
            <Button onClick={() => router.push(`/pricing`)} highlight>
              {t("plans.upgrade")}
            </Button>
          )}
        </div>
      </div>
      {stripeRole && (
        <div className="text-center mt-20">
          <p>
            Got questions? 👋 Shoot us an email at{" "}
            <a
              href="mailto:heythere@moodboardly.com"
              className="text-pink-800 hover:underline"
            >
              heythere@moodboardly.com
            </a>
          </p>
        </div>
      )}
    </>
  )
}
export default SubscriptionDetails

const ProgressBar = ({text, value, max}) => {
  const { t } = useTranslation()

  const percent = Math.round(value/max * 100)
  const width = `${percent}%`

  return (
    <div className="relative pt-1">
      <div className="flex mb-2 items-center justify-between">
        <div>
          <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-gray-700 bg-gray-50">
            {text}
          </span>
        </div>
        <div className="text-right">
          <span className="text-xs font-semibold inline-block text-gray-600">
            {value}/{max}
          </span>
        </div>
      </div>
      <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-100">
        <div
          style={{ width }}
          className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-pink-500"
        />
      </div>
    </div>
  )
}

const TrialBuyManageButton = ({ plan, highlight, isMonthly }) => {
  const auth = useContext(FirebaseContext)
  const { stripeRole, user } = auth
  const { t } = useTranslation()
  const router = useRouter()
  const [isMyroomdesigner, setIsMyroomdesigner] = useState(false)

  const onTrial = () => {
    router.push("/profile?redirect=/pricing")
  }

  if (!user) {
    const buttonText = isMyroomdesigner 
      ? "Start 1-Day Free Trial" 
      : plan?.paymentMode === 'payment'
        ? "Get Lifetime Access"
        : "Subscribe Now"

    return (
      <button 
        onClick={onTrial} 
        className={`w-full ${highlight 
          ? "group relative flex justify-center items-center text-base rounded-full bg-gradient-to-r from-indigo-400 to-indigo-300 text-white hover:ring-2 hover:ring-blue-300 font-heading py-3 px-6"
          : "p-1"
        }`}
      >
        <div className={highlight ? "" : "group relative flex items-center font-heading block py-2 px-5 text-sm sm:text-base rounded-full cursor-pointer font-heading hover:ring-2 ring-2 ring-gray-700 hover:ring-gray-500 mx-auto md:mx-0"}>
          <p className={`relative z-10 font-heading text-base ${highlight ? "" : "text-black"} mx-auto`}>
            {buttonText}
          </p>
        </div>
      </button>
    )
  } else if (stripeRole) {
    return <ManagePlanButton highlight={highlight} />
  }

  const buttonText = isMyroomdesigner
    ? "Start 1-Day Free Trial"
    : plan?.paymentMode === 'payment'
      ? `${t("plans.buy-one")} ${plan?.name}`
      : `${t("plans.upgrade")} to ${plan?.name}`;

  return (
    <BuyPlanButton
      plan={plan}
      text={buttonText}
      highlight={highlight}
      isMonthly={isMonthly}
    />
  )
}
export { TrialBuyManageButton }
